// ./src/components/features/OrganizerProfiles/OrganizerProfilesList/OrganizerProfilesList.tsx
//
// The account's ORGANIZER PAGES (ROADMAP A2b #54), with a different presentation
// per breakpoint, switched live on resize (useAppEnvironment is backed by
// useMediaQuery):
//
// - Desktop (>= md): a compact TABLE — logo · name + public link · status · events · actions.
// - Tablet / mobile (< md): a CARD list.
//
// Interaction (tap-safe, same as every other list here):
// - Rows/cards are NEVER click-to-edit; the Edit button is the only way in.
// - Phones (< sm): swipe-to-reveal. ⚠ Edit/Delete are IDENTITY actions so they sit
//   on the right (swipe left); "View page" is a do-something action, so it is
//   marked `secondary` and SwipeableCard routes it to the left.
//
// Data resolves from <OrganizerProfilesProvider> when props are omitted.

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  Avatar,
  Anchor,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { OrganizerProfile, ORGANIZER_LISTING_STATUS } from "@/utils/api";
import { useOptionalOrganizerProfilesContext, useAppEnvironment } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  buildOrganizerPublicUrl,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Types
// ============================================

export interface OrganizerProfilesListProps {
  /** The profiles to render. Optional when inside the provider. */
  organizerProfiles?: OrganizerProfile[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditProfile?: (profile: OrganizerProfile) => void;
  onDeleteProfile?: (profile: OrganizerProfile) => void;
  isRemoving?: (id?: string) => boolean;
  /** ID of the recently added/edited profile to highlight. */
  highlightedProfileId?: string | null;
  /** Opens the create form from the empty state. */
  onAddProfile?: () => void;
  /**
   * True when a search or filter is narrowing the list.
   *
   * ⚠ REQUIRED FOR CORRECTNESS, not decoration. Without it a search that matches
   * nothing renders "No organizer pages yet — create your first", which tells
   * somebody who has ten of them that their data is gone.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

/** The badge colour for each lifecycle state. */
const STATUS_COLOR: Record<string, string> = {
  [ORGANIZER_LISTING_STATUS.PUBLISHED]: "jade",
  [ORGANIZER_LISTING_STATUS.DRAFT]: "gray",
  [ORGANIZER_LISTING_STATUS.DISABLED]: "orange",
};

const ListingBadge: React.FC<{ profile: OrganizerProfile }> = ({ profile }) => {
  const { t } = useTranslation(["organizerProfiles", "common"]);
  const value = profile.listingStatus || ORGANIZER_LISTING_STATUS.DRAFT;

  return (
    <Badge size="md" variant="light" color={STATUS_COLOR[value] || "gray"}>
      {t(`organizerProfiles:listingStatus.${value}`, { defaultValue: value })}
    </Badge>
  );
};

/** The profile's logo, falling back to its initial. */
const ProfileAvatar: React.FC<{ profile: OrganizerProfile; size: number }> = ({
  profile,
  size,
}) => {
  const url =
    profile.logoUploadedFile?.thumbnailUrl || profile.logoUploadedFile?.fileUrl || null;

  return (
    // ⚠ `objectFit: contain`. Mantine's Avatar covers by default, which centre-crops
    // to a square — and an association's logo is usually a wide lockup, so the name
    // beside the mark gets sliced off. Contain shows the whole logo and letterboxes it.
    <Avatar
      src={url}
      size={size}
      radius="md"
      color="blue"
      imageProps={{ style: { objectFit: "contain" } }}
    >
      {(profile.name || "?").trim().charAt(0).toUpperCase()}
    </Avatar>
  );
};

/**
 * The public link, shown as the address it really is.
 *
 * ⚠ A DRAFT PAGE IS NOT LINKED. `/o/<slug>` refuses to serve a draft, so an anchor
 * here would open a 404 from inside the host's own dashboard — the surface where
 * they are least likely to suspect us and most likely to conclude the feature is
 * broken. Drafts show the address as plain dimmed text instead.
 */
const PublicLink: React.FC<{ profile: OrganizerProfile; compact?: boolean }> = ({
  profile,
  compact,
}) => {
  const url = buildOrganizerPublicUrl(profile.slug);
  const isLive = profile.listingStatus !== ORGANIZER_LISTING_STATUS.DRAFT;
  const label = `/o/${profile.slug || ""}`;

  if (!url || !isLive) {
    return (
      <Text size={compact ? "xs" : "sm"} c="dimmed" lineClamp={1}>
        {label}
      </Text>
    );
  }

  return (
    <Anchor
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      size={compact ? "xs" : "sm"}
      lineClamp={1}
    >
      {label}
    </Anchor>
  );
};

// Always-visible row actions (desktop table + tablet cards).
const RowActions: React.FC<{
  profile: OrganizerProfile;
  onEdit?: (profile: OrganizerProfile) => void;
  onDelete?: (profile: OrganizerProfile) => void;
  isDeleting: boolean;
}> = ({ profile, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["organizerProfiles", "common"]);

  const showEdit = !!onEdit && recordHasRight(profile, "edit");
  const showDelete = !!onDelete && recordHasRight(profile, "remove");
  const publicUrl =
    profile.listingStatus !== ORGANIZER_LISTING_STATUS.DRAFT
      ? buildOrganizerPublicUrl(profile.slug)
      : null;

  if (!showEdit && !showDelete && !publicUrl) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {publicUrl && (
        <Tooltip
          label={t("organizerProfiles:actions.viewPage", { defaultValue: "View page" })}
          withArrow
          position="bottom"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            component="a"
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppIcon iconName="ExternalLink" size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(profile)}
            disabled={isDeleting}
          >
            <AppIcon iconName="Edit" size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(profile)}
            loading={isDeleting}
            disabled={isDeleting}
          >
            <AppIcon iconName="Trash" size={18} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  profile: OrganizerProfile;
  onEdit?: (profile: OrganizerProfile) => void;
  onDelete?: (profile: OrganizerProfile) => void;
  highlighted: boolean;
  isDeleting: boolean;
}

// ============================================
// Desktop: Table row (not clickable)
// ============================================

const ProfileTableRow: React.FC<RowProps> = ({
  profile,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : 1,
        backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      <Table.Td style={{ width: 60 }}>
        <ProfileAvatar profile={profile} size={36} />
      </Table.Td>
      <Table.Td>
        <Text size="md" fw={500} lineClamp={1}>
          {profile.name || "—"}
        </Text>
        <PublicLink profile={profile} />
      </Table.Td>
      <Table.Td style={{ width: 140 }}>
        <ListingBadge profile={profile} />
      </Table.Td>
      <Table.Td style={{ width: 132 }}>
        <RowActions
          profile={profile}
          onEdit={onEdit}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      </Table.Td>
    </Table.Tr>
  );
};

// A skeleton row that IS a real Table.Tr — identical column widths and element
// sizes to ProfileTableRow, so the loading table matches the loaded one.
const ProfileTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td style={{ width: 60 }}>
      <Skeleton height={36} width={36} radius="md" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={18} width="45%" maw={220} mb={8} />
      <Skeleton height={14} width={160} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={22} width={84} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 132 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Mobile / tablet: Card (not clickable)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ProfileCardComponent: React.FC<CardProps> = ({
  profile,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["organizerProfiles", "common"]);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    const publicUrl =
      profile.listingStatus !== ORGANIZER_LISTING_STATUS.DRAFT
        ? buildOrganizerPublicUrl(profile.slug)
        : null;

    if (onEdit && recordHasRight(profile, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(profile),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(profile, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(profile),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    // ⚠ `secondary: true` puts this on the LEFT (swipe right) — the "do something"
    // side. Edit/Delete stay unmarked so they route to the identity side.
    if (publicUrl) {
      actions.push({
        key: "view",
        icon: "ExternalLink",
        label: t("organizerProfiles:actions.viewPage", { defaultValue: "View page" }),
        color: "gray",
        secondary: true,
        onClick: () => window.open(publicUrl, "_blank", "noopener,noreferrer"),
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, profile, isDeleting, t]);

  // showActions=false on phones (swipe reveals them); true on tablet.
  const cardBody = (showActions: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        <ProfileAvatar profile={profile} size={44} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="md" fw={500} lineClamp={1}>
              {profile.name || "—"}
            </Text>
            <ListingBadge profile={profile} />
          </Group>
          <PublicLink profile={profile} compact />
        </Box>
        {showActions && (
          <RowActions
            profile={profile}
            onEdit={onEdit}
            onDelete={onDelete}
            isDeleting={isDeleting}
          />
        )}
      </Group>
    </Paper>
  );

  if (swipeActions.length > 0) {
    return (
      <>
        {/* Phones: swipe to reveal actions (no visible buttons, no tap-to-edit) */}
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        </Box>
        {/* Tablet: always-visible action buttons */}
        <Box visibleFrom="sm">{cardBody(true)}</Box>
      </>
    );
  }

  return cardBody(false);
};

const ProfileCard = React.memo(ProfileCardComponent);

// A skeleton card matching ProfileCardComponent's cardBody.
const ProfileCardSkeleton: React.FC<{ actions: number }> = ({ actions }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <Skeleton height={44} width={44} radius="md" />
      <CardSkeletonLines count={2} sizes={["md", "xs"]} gap={0} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main Component
// ============================================

const OrganizerProfilesListComponent: React.FC<OrganizerProfilesListProps> = ({
  organizerProfiles: profilesProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditProfile,
  onDeleteProfile,
  isRemoving: isRemovingProp,
  highlightedProfileId,
  onAddProfile,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["organizerProfiles", "common"]);
  const { isDesktop } = useAppEnvironment();

  // ── Context vs Props resolution ──
  const context = useOptionalOrganizerProfilesContext();

  const profiles = profilesProp ?? context?.organizerProfiles ?? [];
  const loading = loadingProp ?? context?.loading ?? false;
  const loadingMore = loadingMoreProp ?? context?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? context?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? context?.loadMore;
  const isRemoving = isRemovingProp ?? context?.isRemoving ?? (() => false);

  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  const { hasAnimated } = useEntranceAnimation({ itemCount: profiles.length });

  // ── Wrapped handlers (close any open swipe before acting) ──
  const handleEdit = useCallback(
    (profile: OrganizerProfile) => {
      closeAllCards();
      onEditProfile?.(profile);
    },
    [onEditProfile, closeAllCards]
  );

  const handleDelete = useCallback(
    (profile: OrganizerProfile) => {
      closeAllCards();
      onDeleteProfile?.(profile);
    },
    [onDeleteProfile, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = context?.isLoaded ?? profilesProp !== undefined;
  const showSkeleton = !isLoaded;

  // ── Empty ──
  if (!showSkeleton && profiles.length === 0) {
    // ⚠ A narrowed list that matches nothing is a NO-MATCH state, never the
    // first-run EmptyState — see the prop's comment.
    if (hasQueryNarrowing) {
      return (
        <Paper withBorder p="xl" radius="md">
          <Text ta="center" c="dimmed" size="md">
            {t("organizerProfiles:list.noMatch", {
              defaultValue: "No organizer pages match your search.",
            })}
          </Text>
        </Paper>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("organizerProfiles:emptyState.title", {
            defaultValue: "No organizer pages yet",
          })}
          description={t("organizerProfiles:emptyState.description", {
            defaultValue:
              "An organizer page is your public identity — your name, logo and contact details, with all of your events on it. Put one together and pick it on any event.",
          })}
          iconName="BuildingCommunity"
          actionLabel={t("organizerProfiles:emptyState.actionLabel", {
            defaultValue: "Create an organizer page",
          })}
          onAction={onAddProfile}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (profile: OrganizerProfile): RowProps => ({
    profile,
    onEdit: onEditProfile ? handleEdit : undefined,
    onDelete: onDeleteProfile ? handleDelete : undefined,
    highlighted: profile.id === highlightedProfileId,
    isDeleting: isRemoving(profile.id),
  });

  const loadMore = (
    <LoadMoreButton
      hasMore={hasMore}
      loading={loading}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
    />
  );

  // ── Desktop: table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 60 }} />
                <Table.Th>
                  {t("organizerProfiles:list.columns.name", { defaultValue: "Page" })}
                </Table.Th>
                <Table.Th style={{ width: 140 }}>
                  {t("organizerProfiles:list.columns.status", { defaultValue: "Status" })}
                </Table.Th>
                <Table.Th style={{ width: 132 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 4 }).map((_, i) => (
                    <ProfileTableRowSkeleton key={i} />
                  ))
                : profiles.map((profile) => (
                    <ProfileTableRow key={profile.id} {...rowProps(profile)} />
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile: cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 4 }).map((_, i) => (
          <ProfileCardSkeleton key={i} actions={countCardActions(true /* the public page link */, onEditProfile, onDeleteProfile)} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-sm)",
        }}
      >
        <AnimatePresence mode="sync">
          {profiles.map((profile) => (
            <motion.div
              key={profile.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <ProfileCard
                {...rowProps(profile)}
                forceClose={shouldForceClose(profile.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(profile.id || "")}
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(profile.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const OrganizerProfilesList = React.memo(OrganizerProfilesListComponent);
